import type { SupabaseClient } from '@supabase/supabase-js'

/** One own `daily_tasks` row of a day (RLS "read own", migration 0003). */
export interface DailyRow {
  taskId: string
  progress: number
  /** `completed_at` is set: the task reached its target and was paid. */
  completed: boolean
}

/**
 * What the daily box needs from Supabase (T2.5). `AuthSession.daily` holds one when signed in,
 * null offline. Like `ProfileApi` it only imports types, so supabase-js stays lazy.
 */
export interface DailyApi {
  /** The signed-in user: today's pick is `dailyTasksFor(userId, day)`. */
  userId: string
  /** The own rows of `day` (`YYYY-MM-DD`). Never throws: a failed read is `[]` (progress 0). */
  rows(day: string): Promise<DailyRow[]>
}

type Raw = Record<string, unknown>

export function parseDailyRows(raw: unknown): DailyRow[] {
  if (!Array.isArray(raw)) return []
  return (raw as unknown[])
    .filter((r): r is Raw => typeof r === 'object' && r !== null)
    .filter((r) => typeof r.task_id === 'string')
    .map((r) => ({
      taskId: r.task_id as string,
      progress: typeof r.progress === 'number' && Number.isFinite(r.progress) ? r.progress : 0,
      completed: typeof r.completed_at === 'string' && r.completed_at !== '',
    }))
}

/**
 * The `DailyApi` over a signed-in Supabase client. Only 0001 columns are read, so the same
 * query works before migration 0003: then RLS has no read policy and the answer is `[]`.
 */
export function supabaseDailyApi(
  supabase: SupabaseClient,
  user: string | (() => string),
): DailyApi {
  // A function follows the page's current user (sign-out starts a new guest, T2.6).
  const current = typeof user === 'function' ? user : () => user
  return {
    get userId() {
      return current()
    },
    async rows(day) {
      const userId = current()
      try {
        const res = await supabase
          .from('daily_tasks')
          .select('task_id, progress, completed_at')
          .eq('user_id', userId)
          .eq('date', day)
        if (res.error) {
          console.warn('[daily] read failed', res.error.message)
          return []
        }
        return parseDailyRows(res.data)
      } catch (err) {
        console.warn('[daily] read failed', err instanceof Error ? err.message : err)
        return []
      }
    },
  }
}
